/**
 * The transcript as this app reasons about it: merging what the server says with
 * what the visitor just typed, ordering it, and counting what they have not seen.
 *
 * All pure. Every decision here produces the kind of bug nobody can reproduce —
 * a duplicated message after a slow send, a badge that will not clear, a reply
 * that sorts above the question it answers — and none of them needs a DOM to be
 * wrong.
 */
import type { SeqString, WidgetMessage } from '../api/contract';

/** Before any message exists. `0` is below every real seq, which starts at 1. */
export const ZERO_SEQ: SeqString = '0';

/**
 * Compare two `seq` values.
 *
 * `seq` is an int8 carried as a DECIMAL STRING, because the values outlive
 * `Number.MAX_SAFE_INTEGER`. Both of the obvious shortcuts are wrong: `Number()`
 * loses precision silently at a magnitude no test will ever reach, and comparing
 * the strings directly is lexicographic, which sorts "9" after "10" — a bug that
 * appears on the tenth message of every conversation and looks like a server
 * problem. `BigInt` is exact and costs nothing at this volume.
 */
export function compareSeq(left: SeqString, right: SeqString): number {
  const a = BigInt(left);
  const b = BigInt(right);
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

/**
 * A message the visitor has sent that the server has not yet confirmed.
 *
 * It carries no `seq` and no `id`, because those are the SERVER's to assign, and
 * inventing a temporary one is how a merge ends up with the same message twice.
 * What it does carry is `clientMessageId` — the idempotency key the send used
 * (P2-14) — which is what makes a retry a retry rather than a second message.
 */
export interface PendingMessage {
  readonly clientMessageId: string;
  readonly body: string;
  readonly state: 'sending' | 'failed';
  /** Local clock, used only for ordering within the pending tail. */
  readonly startedAt: number;
}

/**
 * Merge a page of server messages into the ones already on screen.
 *
 * Deduplicated by `id` and ordered by `seq`, and BOTH matter. Polling returns
 * overlapping windows, so without the dedupe the visitor watches their own
 * message appear twice; without the sort, a message that arrives out of order —
 * which happens the moment two agents reply at once, or a scrollback page loads
 * — lands at the bottom regardless of when it was sent.
 *
 * `seq` is monotonic per conversation and assigned by the database in the same
 * transaction as the row, so it is the only ordering key here that is not a
 * clock. `sentAt` is a timestamp, and timestamps tie.
 */
export function mergeMessages(
  existing: readonly WidgetMessage[],
  incoming: readonly WidgetMessage[],
): readonly WidgetMessage[] {
  const byId = new Map<string, WidgetMessage>();
  for (const message of existing) {
    byId.set(message.id, message);
  }
  // Incoming wins on a collision: the server's copy is the authority, and a body
  // that was edited or redacted server-side must not be shadowed by our cache.
  for (const message of incoming) {
    byId.set(message.id, message);
  }
  return [...byId.values()].sort((left, right) => compareSeq(left.seq, right.seq));
}

/**
 * Merge, or REPLACE — the rule that stops two conversations becoming one
 * (P2-52, ADR-0142).
 *
 * `mergeMessages` above is keyed by `id` and ordered by `seq`, and both of those
 * are correct for as long as every message on screen belongs to the same
 * conversation. `seq` is a GAPLESS PER-CONVERSATION ordinal, so it RESTARTS AT 1
 * in a new one — and `closed` is terminal for the visitor, so a new one is
 * exactly what their next message creates.
 *
 * Merging across that boundary produces the bug this function exists to refuse:
 * the old transcript's messages 1, 2, 3 and the new transcript's messages 1, 2
 * are five distinct ids with five seqs that interleave, so the visitor's fresh
 * "hello" sorts above last week's answer and the two conversations are read as
 * one. Nothing about it looks like an error; it looks like the widget scrambled
 * their history.
 *
 * So the caller tells this function whether the incoming page belongs to the
 * conversation already on screen, and a different conversation REPLACES rather
 * than merges. The caller is the only one who can know: it is the only one
 * holding the conversation id the last page carried.
 *
 * The replacement is still SORTED. The server sends oldest first, so this is
 * usually a copy — but "usually" is not an invariant worth trusting when the
 * cost of keeping it is one sort of a list that is at most a page long.
 */
export function reconcileThread(
  current: readonly WidgetMessage[],
  incoming: readonly WidgetMessage[],
  options: { readonly sameConversation: boolean },
): readonly WidgetMessage[] {
  if (options.sameConversation) {
    return mergeMessages(current, incoming);
  }
  return [...incoming].sort((left, right) => compareSeq(left.seq, right.seq));
}

/** The highest sequence number on screen, or `ZERO_SEQ` for an empty transcript. */
export function latestSeq(messages: readonly WidgetMessage[]): SeqString {
  let highest = ZERO_SEQ;
  for (const message of messages) {
    if (compareSeq(message.seq, highest) > 0) {
      highest = message.seq;
    }
  }
  return highest;
}

/** The lowest sequence number on screen — the cursor a scrollback page asks before. */
export function earliestSeq(messages: readonly WidgetMessage[]): SeqString | null {
  let lowest: SeqString | null = null;
  for (const message of messages) {
    if (lowest === null || compareSeq(message.seq, lowest) < 0) {
      lowest = message.seq;
    }
  }
  return lowest;
}

/**
 * How many replies the visitor has not seen.
 *
 * Only `outbound` counts. A visitor's own message is not unread to them, and
 * there is no third case to worry about: an internal note is not in this array
 * and never was — `rlsr_message__app_runtime__visitor_scope` is a RESTRICTIVE
 * policy, so the row was never in the result set the API read. This function
 * does not filter internal notes out, because a filter here would imply one
 * could arrive.
 */
export function unreadCount(messages: readonly WidgetMessage[], lastSeenSeq: SeqString): number {
  let count = 0;
  for (const message of messages) {
    if (message.direction === 'outbound' && compareSeq(message.seq, lastSeenSeq) > 0) {
      count += 1;
    }
  }
  return count;
}

/**
 * Retire pending messages the server has now shown us.
 *
 * A HEURISTIC, and it is worth being precise about why. The exact answer would be
 * to match on `clientMessageId`, but `WidgetMessage` does not carry one — the
 * schema returns the visitor's own message without the key that produced it. So
 * this matches an inbound server message against the OLDEST pending entry with
 * the same body, one for one.
 *
 * The "one for one" is the part that stops it being wrong. A visitor who sends
 * "ok" twice has two pending entries; a naive `filter` on body would clear both
 * the moment the first server copy arrived, and the second message would vanish
 * from the screen until its own response landed. Consuming one pending entry per
 * server message keeps the count on screen correct at every intermediate state.
 *
 * `failed` entries are never cleared here. A send that failed keeps the visitor's
 * words and its retry button until the visitor or a successful retry says
 * otherwise — losing what someone typed is the one thing a chat widget must not
 * do. Their normal exit is the send response, which clears by `clientMessageId`
 * and is exact.
 */
export function clearConfirmed(
  pending: readonly PendingMessage[],
  serverMessages: readonly WidgetMessage[],
): readonly PendingMessage[] {
  const unmatched = [...pending];

  for (const message of serverMessages) {
    if (message.direction !== 'inbound') {
      continue;
    }
    const index = unmatched.findIndex(
      (candidate) => candidate.state === 'sending' && candidate.body === message.body,
    );
    if (index >= 0) {
      unmatched.splice(index, 1);
    }
  }

  return unmatched;
}

/** Clear one pending entry by its idempotency key — the exact path, used on a send response. */
export function clearByClientMessageId(
  pending: readonly PendingMessage[],
  clientMessageId: string,
): readonly PendingMessage[] {
  return pending.filter((message) => message.clientMessageId !== clientMessageId);
}

/**
 * Split a message body into paragraphs for rendering.
 *
 * Every paragraph becomes a TEXT NODE. `bodyFormat` may say `markdown` or `html`,
 * and this app renders both as plain text on purpose: there is no
 * `dangerouslySetInnerHTML` in this repository, and the day rich bodies are
 * shown, they arrive as the branded `SafeHtml` the server sanitiser produces
 * (ADR-0019) — a different type, deliberately, so that the change is a decision
 * rather than a regression.
 */
export function bodyParagraphs(body: string): readonly string[] {
  return body
    .split(/\r?\n\s*\r?\n/u)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== '');
}

/**
 * The highest `seq` among the BUSINESS's messages — P2-51.
 *
 * `direction === 'outbound'` is the widget's word for "from the business", the
 * same predicate `unreadCount` uses one function up. A visitor's own message is
 * `inbound` and must never chime: a chat that beeped at you for typing would be
 * turned off inside a minute, and it would beep on the optimistic echo too.
 *
 * SEPARATE FROM `latestSeq` rather than a parameter on it. The two answer
 * different questions — "where is the transcript" versus "what has the business
 * said" — and a single function with a filter argument would let a caller ask
 * the first while getting the second by forgetting an argument.
 */
export function latestOutboundSeq(messages: readonly WidgetMessage[]): SeqString {
  let highest = ZERO_SEQ;
  for (const message of messages) {
    if (message.direction === 'outbound' && compareSeq(message.seq, highest) > 0) {
      highest = message.seq;
    }
  }
  return highest;
}
